"use client";

import { useTranslations } from "next-intl";

import { type GoalStack } from "@/config/peptide-stacks";
import { cn } from "@/lib/utils";
import { Button } from "@/ui/components/ui/button";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface GoalStackCardsProps {
	goalStacks: GoalStack[];
	activeGoal: string | null;
	selectedSlugs: string[];
	onLoadStack: (peptides: string[]) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GoalStackCards({ goalStacks, activeGoal, selectedSlugs, onLoadStack }: GoalStackCardsProps) {
	const t = useTranslations("stackBuilder");

	// Filter to only show the active goal's stack, or all if no goal selected
	const visibleStacks = activeGoal ? goalStacks.filter((s) => s.goal === activeGoal) : goalStacks;

	if (visibleStacks.length === 0) return null;

	return (
		<div>
			<h2 className="mb-3 text-sm font-semibold text-foreground">Pre-built Research Stacks</h2>

			<div className="flex gap-3 overflow-x-auto pb-1 sm:grid sm:grid-cols-2 sm:overflow-x-visible lg:grid-cols-3">
				{visibleStacks.map((stack) => {
					const isLoaded =
						stack.peptides.length > 0 &&
						stack.peptides.every((slug) => selectedSlugs.includes(slug)) &&
						selectedSlugs.length === stack.peptides.length;

					return (
						<div
							key={stack.goal}
							className={cn(
								"flex min-w-[220px] flex-col gap-2 rounded-xl border p-4 sm:min-w-0",
								"to-muted/30 bg-gradient-to-br from-card",
								"transition-shadow hover:shadow-sm",
								isLoaded ? "border-accent" : "border-border",
							)}
						>
							<h3 className="text-sm font-semibold text-foreground">
								{t(`goalStacks.${stack.descriptionKey}.name`)}
							</h3>

							<p className="text-xs leading-snug text-muted-foreground">
								{t(`goalStacks.${stack.descriptionKey}.description`)}
							</p>

							<p className="text-muted-foreground/70 text-xs">{stack.peptides.join(", ")}</p>

							<Button
								variant={isLoaded ? "default" : "outline-solid"}
								size="sm"
								className="mt-auto w-full text-xs"
								onClick={() => onLoadStack(stack.peptides)}
								disabled={isLoaded}
							>
								{isLoaded ? t("customize") : t("loadStack")}
							</Button>
						</div>
					);
				})}
			</div>
		</div>
	);
}
